/**
 * Primeros pasos: lo que un club recién llegado tiene que montar · 03-11-2026
 *
 * El primer club de verdad fue a crear un grupo y no tenía sede; nada en el panel le
 * había dicho que la sede iba antes. Inicio enseñaba lo pendiente —cosas que se
 * tuercen en un club que ya funciona— y a un club vacío le decía «Todo al día».
 *
 * Esto es el orden en que se monta un club, cada paso con la pantalla donde se hace.
 * Las cuentas vienen de `setup` en `GET /clubs/{club}/today`; **el orden, los textos
 * y los destinos son del panel**, porque las rutas son del panel.
 *
 * - **Sede → grupos → horarios**: cada grupo entrena en una sede, y sin horario un
 *   grupo no genera clases ni se pasa lista en él.
 * - **Tarifas**, solo a quien ve el dinero (`puedeVerCuota`).
 * - **Alumnos**, y **la ficha del buscador** al final: es lo que trae familias nuevas,
 *   y con lo anterior hecho ya hay algo que enseñar.
 *
 * Cuando están todos, la tarjeta desaparece sola.
 */
import type { NombreDeIcono } from '@/shared/ui/Icono';
import type { MontajeDelClub } from '@/club/pendientes';

export interface PasoInicial {
  readonly clave:
    'sede' | 'grupos' | 'horarios' | 'tarifas' | 'alumnos' | 'ficha';
  readonly hecho: boolean;
  readonly icono: NombreDeIcono;
  readonly titulo: string;
  readonly detalle: string;
  readonly accion: string;
  readonly destino: string;
}

export function primerosPasos(
  m: MontajeDelClub,
  { conTarifas }: { conTarifas: boolean }
): readonly PasoInicial[] {
  const sinHorario = m.groups_without_schedule;

  const pasos: (PasoInicial | null)[] = [
    {
      clave: 'sede',
      hecho: m.venues > 0,
      icono: 'sede',
      titulo: 'Añade la sede donde entrenáis',
      detalle:
        'Cada grupo entrena en una, y con su dirección el club sale en el mapa del buscador.',
      accion: 'Añadir la sede',
      destino: '/club/sedes?nuevo=1',
    },
    {
      clave: 'grupos',
      hecho: m.groups > 0,
      icono: 'grupos',
      titulo: 'Crea tus grupos',
      detalle:
        'Infantil, adultos, competición…: a ellos se matriculan los alumnos.',
      accion: 'Crear un grupo',
      destino: '/grupos?nuevo=1',
    },
    {
      clave: 'horarios',
      hecho: m.groups > 0 && sinHorario === 0,
      icono: 'reloj',
      titulo:
        sinHorario === 0
          ? 'Ponles horario'
          : sinHorario === 1
            ? 'Un grupo no tiene horario'
            : `${sinHorario} grupos no tienen horario`,
      detalle:
        'Días y horas de cada grupo: de ahí salen las clases del calendario y la lista.',
      accion: 'Ponerle horario',
      destino: '/grupos',
    },
    conTarifas
      ? {
          clave: 'tarifas',
          hecho: m.fee_plans > 0,
          icono: 'euro',
          titulo: 'Pon tus tarifas',
          detalle:
            'Lo que cuesta al mes, al trimestre o al curso. Luego se asigna a cada alumno.',
          accion: 'Crear una tarifa',
          destino: '/cobros/tarifas?nuevo=1',
        }
      : null,
    {
      clave: 'alumnos',
      hecho: m.students > 0,
      icono: 'alumnos',
      titulo: 'Da de alta a tus alumnos',
      detalle:
        'Uno a uno, o todos de una vez desde la hoja de cálculo que ya tengas.',
      accion: 'Añadir alumnos',
      destino: '/alumnos?nuevo=1',
    },
    {
      clave: 'ficha',
      hecho: m.profile_published,
      icono: 'buscador',
      titulo: 'Publica la ficha del club',
      detalle:
        'Para que te encuentren las familias que buscan club en kiapclubs.com.',
      accion: 'Publicarla',
      destino: '/club/ficha',
    },
  ];

  return pasos.filter((p): p is PasoInicial => p !== null);
}

/** Lo que está hecho, de cuántos. */
export const progreso = (pasos: readonly PasoInicial[]) => ({
  hechos: pasos.filter((p) => p.hecho).length,
  total: pasos.length,
});

/** El primero sin hacer: el que se destaca. `undefined` cuando ya no queda nada. */
export const siguiente = (pasos: readonly PasoInicial[]) =>
  pasos.find((p) => !p.hecho);
